import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { UnifiedFeedProvider } from './contexts/UnifiedFeedContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PostsProvider } from './contexts/PostsContext';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const VerificationPage = lazy(() => import('./pages/VerificationPage'));
const BurningPage = lazy(() => import('./pages/BurningPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const APIAccessPage = lazy(() => import('./pages/APIAccessPage'));
const TwoFactorPage = lazy(() => import('./pages/TwoFactorPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PostDetailPage = lazy(() => import('./pages/PostDetailPage'));
const PersonalVerificationPage = lazy(
  () => import('./pages/PersonalVerificationPage')
);
const BusinessVerificationPage = lazy(
  () => import('./pages/BusinessVerificationPage')
);
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const HelpCentrePage = lazy(() => import('./pages/HelpCentrePage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));
const AdsInfoPage = lazy(() => import('./pages/AdsInfoPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BrandResourcesPage = lazy(() => import('./pages/BrandResourcesPage'));
const AdvertisingPage = lazy(() => import('./pages/AdvertisingPage'));
const BusinessPage = lazy(() => import('./pages/BusinessPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className='flex justify-center items-center min-h-screen'>
    <div className='w-8 h-8 rounded-full border-b-2 border-green-600 animate-spin'></div>
  </div>
);

// Component to conditionally render landing page or home page
const ConditionalHome = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <div className='w-8 h-8 rounded-full border-b-2 border-green-600 animate-spin'></div>
      </div>
    );
  }

  return user ? <HomePage /> : <LandingPage />;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <UnifiedFeedProvider>
            <PostsProvider>
              <Router>
                <ScrollToTop />
                <div className='flex overflow-x-hidden flex-col w-full max-w-full min-h-screen'>
                  <ErrorBoundary>
                    <Header />
                  </ErrorBoundary>
                  <main className='overflow-x-hidden flex-1 w-full max-w-full'>
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path='/' element={<ConditionalHome />} />
                          <Route path='/login' element={<LoginPage />} />
                          <Route path='/register' element={<RegisterPage />} />
                          <Route
                            path='/auth/callback'
                            element={<AuthCallbackPage />}
                          />
                          <Route path='/profile' element={<ProfilePage />} />
                          <Route
                            path='/notifications'
                            element={
                              <ProtectedRoute>
                                <NotificationsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path='/verification'
                            element={
                              <ProtectedRoute>
                                <VerificationPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path='/verification/personal'
                            element={
                              <ProtectedRoute>
                                <PersonalVerificationPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path='/verification/business'
                            element={
                              <ProtectedRoute>
                                <BusinessVerificationPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route path='/burning' element={<BurningPage />} />
                          <Route path='/about' element={<AboutPage />} />
                          <Route path='/help' element={<HelpCentrePage />} />
                          <Route
                            path='/privacy'
                            element={<PrivacyPolicyPage />}
                          />
                          <Route
                            path='/terms'
                            element={<TermsOfServicePage />}
                          />
                          <Route
                            path='/cookies'
                            element={<CookiePolicyPage />}
                          />
                          <Route
                            path='/accessibility'
                            element={<AccessibilityPage />}
                          />
                          <Route path='/ads' element={<AdsInfoPage />} />
                          <Route path='/blog' element={<BlogPage />} />
                          <Route
                            path='/brand'
                            element={<BrandResourcesPage />}
                          />
                          <Route
                            path='/advertising'
                            element={<AdvertisingPage />}
                          />
                          <Route path='/business' element={<BusinessPage />} />
                          <Route path='/news' element={<NewsPage />} />
                          <Route path='/settings' element={<SettingsPage />} />
                          <Route path='/support' element={<SupportPage />} />
                          <Route path='/api' element={<APIAccessPage />} />
                          <Route path='/2fa' element={<TwoFactorPage />} />
                          <Route
                            path='/forgot-password'
                            element={<ForgotPasswordPage />}
                          />
                          <Route
                            path='/reset-password'
                            element={<ResetPasswordPage />}
                          />
                          <Route
                            path='/post/:postId'
                            element={<PostDetailPage />}
                          />
                        </Routes>
                      </Suspense>
                    </ErrorBoundary>
                  </main>
                  <ErrorBoundary>
                    <Footer />
                  </ErrorBoundary>
                </div>
                <Toaster position='top-right' />
              </Router>
            </PostsProvider>
          </UnifiedFeedProvider>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
